import React from 'react';
import {
  Stack,
  Typography,
  TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { format, startOfDay } from 'date-fns';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { refundExternalCharge } from 'services/requests/contract';
import { toast } from 'react-toastify';

const ExternalChargeRefundForm = ({ externalChargeId, onSuccess, onClose }) => {
  const validationSchema = yup.object({
    externalId: yup.string(),
    refundDatetime: yup.string()
      .required('Data do reembolso é obrigatória'),
    observation: yup.string()
  });

  const getInitialValues = () => {
    const currentDate = new Date();
    const formattedDate = format(startOfDay(currentDate), 'yyyy-MM-dd');
    
    return {
      externalId: '',
      refundDatetime: formattedDate,
      observation: ''
    };
  };

  const handleFormSubmit = async (values, actions) => {
    try {
      if (!values.refundDatetime) {
        actions.setErrors({ refundDatetime: 'Data do reembolso é obrigatória' });
        return;
      }

      const dateObject = startOfDay(new Date(values.refundDatetime));
      
      if (isNaN(dateObject.getTime())) {
        actions.setErrors({ refundDatetime: 'Data inválida' });
        return;
      }

      const body = {
        externalId: values.externalId || undefined,
        refundDatetime: dateObject,
        observation: values.observation || undefined
      };

      await refundExternalCharge({
        externalChargeId,
        body
      });

      toast('Reembolso registrado com sucesso!', 'success');
      onSuccess?.();
      onClose?.();
      
    } catch (error) {
      console.error(error);
      toast(error.message || 'Erro ao registrar reembolso', 'error');
      if (actions && actions.setErrors) {
        actions.setErrors({ submit: error.message });
      }
    } finally {
      if (actions && actions.setSubmitting) {
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched, actions }) => (
        <Form>
          <Stack direction="column" spacing={3} sx={{ 
            minWidth: { xs: '100%', sm: 400 },
            maxWidth: '100%'
          }}>
            <Typography variant="body2" color="text.secondary">
              Registre o reembolso da cobrança externa.
            </Typography>

            <DatePicker
              label="Data do Reembolso"
              value={values.refundDatetime}
              onChange={(selectedDate) => {
                if (selectedDate) {
                  const formattedDate = format(startOfDay(new Date(selectedDate)), 'yyyy-MM-dd');
                  setFieldValue('refundDatetime', formattedDate);
                } else {
                  const currentDate = format(startOfDay(new Date()), 'yyyy-MM-dd');
                  setFieldValue('refundDatetime', currentDate);
                }
              }}
              inputFormat="DD/MM/YYYY"
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  helperText={touched.refundDatetime && errors.refundDatetime ? errors.refundDatetime : "Data em que o reembolso foi realizado"}
                  error={touched.refundDatetime && Boolean(errors.refundDatetime)}
                  required
                />
              )}
              maxDate={new Date()}
            />

            <TextField
              fullWidth
              label="ID Externo do Reembolso (Opcional)"
              value={values.externalId}
              onChange={(e) => setFieldValue('externalId', e.target.value)}
              helperText="Identificador externo do reembolso (opcional)"
            />

            <TextField
              fullWidth
              label="Observação (Opcional)"
              multiline
              rows={3}
              value={values.observation}
              onChange={(e) => setFieldValue('observation', e.target.value)}
              helperText="Observações sobre o reembolso"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <LoadingButton
                type="submit"
                variant="contained"
                color="success"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Registrar Reembolso
              </LoadingButton>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default ExternalChargeRefundForm;